/**
 * The workspace object registry (ADR-001 D5). One entry per linkable object
 * type; the link graph, global search, @-mentions and the MCP server all read
 * from it, so adding a type is one entry here plus widening relay.links'
 * from_type/to_type CHECK in a Forge migration.
 *
 * Dependency-free on purpose (no React, no Supabase client): the Cloudflare
 * Worker imports it too.
 */
import { noteBodyToPlain } from "./mentions";
import { WORKSPACE_OBJECT_TYPES, isWorkspaceObjectType, type WorkspaceObjectType } from "./workspace-object-types";

export { WORKSPACE_OBJECT_TYPES, isWorkspaceObjectType, type WorkspaceObjectType };

export type WorkspaceObjectRow = Record<string, unknown>;

export interface WorkspaceObjectDef {
  type: WorkspaceObjectType;
  label: string;
  pluralLabel: string;
  /** Postgres schema of `table`. Projects are Forge-owned public.projects rows. */
  schema: "relay" | "public";
  table: string;
  /** Columns to select when resolving a row's label and route (always includes id). */
  select: string;
  /** Rows with this column set are soft-deleted and never resolve. */
  softDeleteColumn?: "deleted_at";
  /** relay.activities discriminator for the activity-backed types. */
  activityType?: "note" | "task";
  labelOf: (row: WorkspaceObjectRow) => string;
  routeOf: (row: WorkspaceObjectRow) => string;
  /** Global search (D6): one query per type, generated from this. */
  search: {
    /** Columns matched with a case-insensitive contains. */
    columns: readonly string[];
    order: { column: string; ascending: boolean; nullsFirst?: boolean };
    /** Columns that must be null, e.g. an open task's completed_at. */
    isNull?: readonly string[];
    sublabelOf?: (row: WorkspaceObjectRow) => string | undefined;
  };
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function firstLine(value: unknown, max = 80): string {
  const line = text(value).split("\n")[0]?.trim() ?? "";
  return line.length > max ? `${line.slice(0, max - 1)}…` : line;
}

/**
 * A note attached to a record renders in that record's activity timeline;
 * standalone notes live on /notes. Deal first: a note written on a deal also
 * carries the deal's contact_id. (Contact `?tab=notes` is the contact's own
 * notes field, not activity notes.)
 */
function noteRoute(row: WorkspaceObjectRow): string {
  if (typeof row.deal_id === "string") return `/deals/${row.deal_id}`;
  if (typeof row.contact_id === "string") return `/contacts/${row.contact_id}?tab=activity`;
  if (typeof row.company_id === "string") return `/companies/${row.company_id}`;
  if (typeof row.related_project_id === "string") return `/projects/${row.related_project_id}?tab=activity`;
  return `/notes?note=${String(row.id)}`;
}

export const WORKSPACE_OBJECTS: Record<WorkspaceObjectType, WorkspaceObjectDef> = {
  contact: {
    type: "contact",
    label: "Contact",
    pluralLabel: "Contacts",
    schema: "relay",
    table: "contacts",
    select: "id, first_name, last_name, email",
    softDeleteColumn: "deleted_at",
    labelOf: (row) =>
      [text(row.first_name), text(row.last_name)].filter(Boolean).join(" ") || text(row.email) || "Unnamed",
    routeOf: (row) => `/contacts/${String(row.id)}`,
    search: {
      columns: ["first_name", "last_name", "email", "company"],
      order: { column: "updated_at", ascending: false },
      // The email under a name; a nameless contact already shows its email.
      sublabelOf: (row) => (text(row.first_name) || text(row.last_name) ? text(row.email) || undefined : undefined),
    },
  },
  company: {
    type: "company",
    label: "Company",
    pluralLabel: "Companies",
    schema: "relay",
    table: "companies",
    select: "id, name",
    softDeleteColumn: "deleted_at",
    labelOf: (row) => text(row.name) || "Unnamed company",
    routeOf: (row) => `/companies/${String(row.id)}`,
    search: { columns: ["name"], order: { column: "name", ascending: true } },
  },
  deal: {
    type: "deal",
    label: "Deal",
    pluralLabel: "Deals",
    schema: "relay",
    table: "deals",
    select: "id, title",
    softDeleteColumn: "deleted_at",
    labelOf: (row) => text(row.title) || "Untitled deal",
    routeOf: (row) => `/deals/${String(row.id)}`,
    search: { columns: ["title"], order: { column: "updated_at", ascending: false } },
  },
  project: {
    type: "project",
    label: "Project",
    pluralLabel: "Projects",
    schema: "public",
    table: "projects",
    select: "id, name",
    softDeleteColumn: "deleted_at",
    labelOf: (row) => text(row.name) || "Untitled project",
    routeOf: (row) => `/projects/${String(row.id)}`,
    search: { columns: ["name"], order: { column: "created_at", ascending: false } },
  },
  document: {
    type: "document",
    label: "Document",
    pluralLabel: "Documents",
    schema: "relay",
    table: "documents",
    select: "id, title, display_id",
    softDeleteColumn: "deleted_at",
    labelOf: (row) => [text(row.display_id), text(row.title)].filter(Boolean).join(" · ") || "Untitled document",
    routeOf: (row) => `/documents/${String(row.id)}`,
    search: { columns: ["title", "display_id"], order: { column: "updated_at", ascending: false } },
  },
  note: {
    type: "note",
    label: "Note",
    pluralLabel: "Notes",
    schema: "relay",
    table: "activities",
    select: "id, body, contact_id, deal_id, company_id, related_project_id",
    activityType: "note",
    // A note's mentions read as "@Label" here too, never as @[Label](relay://…) markup.
    labelOf: (row) => firstLine(noteBodyToPlain(text(row.body))) || "Untitled note",
    routeOf: noteRoute,
    search: { columns: ["body"], order: { column: "created_at", ascending: false } },
  },
  task: {
    type: "task",
    label: "Task",
    pluralLabel: "Tasks",
    schema: "relay",
    table: "activities",
    select: "id, title",
    activityType: "task",
    labelOf: (row) => text(row.title) || "Untitled task",
    routeOf: (row) => `/tasks?task=${String(row.id)}`,
    // Open tasks only, soonest due first (undated last).
    search: {
      columns: ["title"],
      order: { column: "due_at", ascending: true, nullsFirst: false },
      isNull: ["completed_at"],
    },
  },
};

